import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Drawer,
  Row,
  Col,
  Select,
} from 'antd';

import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import { getSourceFieldMap } from './workflowHelpers';

const { Option } = Select;

// PO fields based on backend implementation in sageIntacctPO.ts
const PO_FIELDS = [
  {
    key: 'materialTable',
    label: 'Material Table',
    required: true,
    isTableSelect: true,
  },
  {
    key: 'vendorId',
    label: 'Vendor',
    required: true,
    description: 'Vendor from material table row',
    isMaterialTableColumn: true,
  },
  {
    key: 'projectId',
    label: 'Project',
    required: false,
    description: 'Project dropdown field from the form',
    isProjectField: true,
  },
  {
    key: 'description',
    label: 'Description',
    required: false,
    description: 'Line item description',
    isMaterialTableColumn: true,
  },
  {
    key: 'quantity',
    label: 'Quantity',
    required: false,
    description: 'Item quantity',
    isMaterialTableColumn: true,
  },
  {
    key: 'unitPrice',
    label: 'Unit Price',
    required: false,
    description: 'Price per unit',
    isMaterialTableColumn: true,
  },
  {
    key: 'attachment',
    label: 'Attachment',
    required: false,
    description: 'Attachment from material table',
    isMaterialTableColumn: true,
  },
];

function WorkflowSageIntacctPOConfigureDrawer({
  visible,
  onClose,
  onSubmit,
  sourceName,
  sourceSections = [],
  fieldMappings = {},
  onFieldMappingsChange,
  materialTableFields = [],
}) {
  // Get all columns from the selected material table
  const materialTableColumns = useMemo(() => {
    if (!fieldMappings.materialTable) return [];

    const selectedTable = sourceSections
      .flatMap((section) => section.fields || [])
      .find((field) => field.id === fieldMappings.materialTable);

    if (!selectedTable?.configProps?.columns) return [];

    return selectedTable.configProps.columns.map((column) => ({
      label: column.title || column.name,
      value: column.key,
    }));
  }, [fieldMappings.materialTable, sourceSections]);

  // Get source field map for project dropdowns
  const sourceFieldMap = useMemo(() => getSourceFieldMap(sourceSections), [sourceSections]);

  const projectDropdowns = sourceFieldMap['dropdown - Projects'] || [];
  const projectFields = projectDropdowns.map((field) => ({
    label: field.title,
    value: field.id,
  }));

  const handleFieldChange = (fieldKey) => (value) => {
    const newMappings = { ...fieldMappings };
    if (value) {
      newMappings[fieldKey] = value;
    } else {
      delete newMappings[fieldKey];
    }
    onFieldMappingsChange(newMappings);
  };

  return (
    <Drawer
      title={(
        <span>
          Configure which fields from this
          {' '}
          <b>{sourceName}</b>
          {' '}
          form are loaded into the Sage Intacct Purchase Order
        </span>
      )}
      visible={visible}
      onClose={onClose}
      width={600}
      footer={(
        <Row justify="end" gutter={10}>
          <Col>
            <OnTraccrButton
              title="Cancel"
              onClick={onClose}
            />
          </Col>
          <Col>
            <OnTraccrButton
              title="Done"
              type="primary"
              onClick={onSubmit}
            />
          </Col>
        </Row>
      )}
    >
      <div style={{ padding: '0 24px' }}>
        {PO_FIELDS.map((field) => {
          const { key, label, required, isTableSelect, isProjectField, isMaterialTableColumn, description } = field;

          // Show material table selector first
          if (isTableSelect) {
            return (
              <div key={key} style={{ marginBottom: 20 }}>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontWeight: 'bold' }}>
                    {label}
                    {required && <span style={{ color: 'red' }}> *</span>}
                  </span>
                  {description && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      {description}
                    </div>
                  )}
                </div>
                <Select
                  value={fieldMappings[key]}
                  onChange={handleFieldChange(key)}
                  placeholder={`Select ${label}`}
                  style={{ width: '100%' }}
                  allowClear
                >
                  {materialTableFields.map((tableField) => (
                    <Option key={tableField.value} value={tableField.value}>
                      {tableField.label}
                    </Option>
                  ))}
                </Select>
              </div>
            );
          }

          // Show project field selector (from form-level dropdown fields)
          if (isProjectField) {
            return (
              <div key={key} style={{ marginBottom: 20 }}>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontWeight: 'bold' }}>
                    {label}
                    {required && <span style={{ color: 'red' }}> *</span>}
                  </span>
                  {description && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      {description}
                    </div>
                  )}
                </div>
                <Select
                  value={fieldMappings[key]}
                  onChange={handleFieldChange(key)}
                  placeholder={`Select ${label}`}
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {projectFields.map((projectField) => (
                    <Option key={projectField.value} value={projectField.value}>
                      {projectField.label}
                    </Option>
                  ))}
                </Select>
              </div>
            );
          }

          // Only show material table column mappings if a material table is selected
          if (!isMaterialTableColumn || !fieldMappings.materialTable) {
            return null;
          }

          return (
            <div key={key} style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 'bold' }}>
                  {label}
                  {required && <span style={{ color: 'red' }}> *</span>}
                </span>
                {description && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                    {description}
                  </div>
                )}
              </div>
              <Select
                value={fieldMappings[key]}
                onChange={handleFieldChange(key)}
                placeholder={`Select column for ${label}`}
                style={{ width: '100%' }}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {materialTableColumns.map((column) => (
                  <Option key={column.value} value={column.value}>
                    {column.label}
                  </Option>
                ))}
              </Select>
            </div>
          );
        })}
        {!fieldMappings.materialTable && (
          <div style={{
            padding: 20,
            backgroundColor: '#f5f5f5',
            borderRadius: 4,
            textAlign: 'center',
            color: '#888',
          }}>
            Please select a Material Table first to configure material table field mappings
          </div>
        )}
      </div>
    </Drawer>
  );
}

WorkflowSageIntacctPOConfigureDrawer.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  sourceName: PropTypes.string.isRequired,
  sourceSections: PropTypes.array.isRequired,
  fieldMappings: PropTypes.object.isRequired,
  onFieldMappingsChange: PropTypes.func.isRequired,
  materialTableFields: PropTypes.array.isRequired,
};

WorkflowSageIntacctPOConfigureDrawer.defaultProps = {
  visible: false,
};

export default WorkflowSageIntacctPOConfigureDrawer;
